import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { defaultRenderOption, renderTriggerContent } from './MemberPickerInternal.js';
import { useFloatingPosition } from '@cortex/web-design-system/hooks/useFloatingPosition';
import { useMemberList } from '../../../hooks/useMember.js';

/**
 * 単一メンバーを表す最小構造。
 * id / name は必須。それ以外は任意のメタ情報。
 */
export interface Member {
  /** 一意キー (selection 状態のキーとして利用) */
  id: string;
  /** 表示名 */
  name: string;
  /** 補助表示 (メールアドレス・ロール等。リストで二行目に出る) */
  description?: string;
  /** 旧称互換用 */
  email?: string;
  /** アバター画像 URL */
  avatarUrl?: string;
  /** 所属部署 (department whitelist で使用) */
  department?: string;
  /** この項目を選択不可にする */
  disabled?: boolean;
}

interface BaseProps {
  /** 外部から直接渡す候補一覧。未指定時は useMemberList の取得結果を使う */
  members?: readonly Member[];
  /**
   * 候補に含める email whitelist。指定すると MemberPicker 自身が取得した member のうち
   * `member.email` が一致するものだけを表示する。
   */
  allowedEmails?: readonly string[];
  /** プレースホルダー文言 (未選択時に表示) */
  placeholder?: string;
  /** ラベル */
  label?: string;
  /** ヘルプテキスト (error 指定時は非表示) */
  helperText?: string;
  /** エラーメッセージ (指定で error スタイルになる) */
  error?: string;
  /** 全体を無効化 */
  disabled?: boolean;
  /** 検索フォームを表示するか (default: true) */
  searchable?: boolean;
  /** 検索フォームの placeholder */
  searchPlaceholder?: string;
  /** 検索ヒットが 0 件のときの文言 */
  emptyMessage?: string;
  /** リストの最大高さ (px) */
  maxListHeight?: number;
  /**
   * 候補に含める部署 whitelist。指定すると `member.department` が一致するメンバーだけが
   * リスト・検索結果に登場する (例: PDG チーム用ツールで PDG 以外を出さない)。
   * 空配列を渡した場合は誰も表示されない。未指定 (`undefined`) は無制限。
   */
  departments?: readonly string[];
  /** カスタムフィルタ。指定時はデフォルトの name/description/email/id マッチを上書き */
  filterFn?: (member: Member, query: string) => boolean;
  /** member ごとの選択不可判定 */
  isMemberDisabled?: (member: Member) => boolean;
  /** 行表示のカスタムレンダリング */
  renderOption?: (member: Member) => ReactNode;
  /** 追加 className (ルート要素) */
  className?: string;
  /** 追加 style (ルート要素) */
  style?: CSSProperties;
  /** トリガー要素の id (label の for と紐づけ) */
  id?: string;
  /** ARIA ラベル (label prop が無い時に必須) */
  'aria-label'?: string;
}

interface SingleProps extends BaseProps {
  /** false / 未指定で単一選択 */
  multiple?: false;
  /** 選択中の id (未選択時 null) */
  value: string | null;
  /** 選択変更ハンドラ */
  onChange: (value: string | null) => void;
}

interface MultiProps extends BaseProps {
  /** true で複数選択モード */
  multiple: true;
  /** 選択中の id 配列 */
  value: readonly string[];
  /** 選択変更ハンドラ */
  onChange: (value: string[]) => void;
}

type MemberPickerProps = SingleProps | MultiProps;

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business Product Innovation Lab の GitHub Activity / 活動レポートで member target を選択するドロップダウン。単一/複数選択・検索・キーボード操作 (上下キー / Enter / Escape) に対応する
 * @graph-connects useMemberList [calls] メンバーリスト取得
 */
export function MemberPicker(props: MemberPickerProps): ReactNode {
  const memberListQuery = useMemberList({ organization: 'all' });
  const fetchedMembers = useMemo<readonly Member[]>(() => {
    const data = memberListQuery.data;
    if (data === undefined) return [];
    const merged = [...data.engineers, ...data.qa];
    return merged.map((m) => ({
      id: m.email,
      name: m.nickName ?? m.name,
      description: m.email,
      department: m.teamName,
      avatarUrl: m.avatarUrl ?? undefined,
    }));
  }, [memberListQuery.data]);
  const members = props.members ?? fetchedMembers;

  return <MemberPickerContent {...props} fetchedMembers={members} />;
}

/** @graph-connects none */
type MemberPickerContentProps = MemberPickerProps & { fetchedMembers: readonly Member[] };

/** @graph-connects none */
function MemberPickerContent(props: MemberPickerContentProps): ReactNode {
  const {
    fetchedMembers,
    allowedEmails,
    placeholder = '選択してください',
    label,
    helperText,
    error,
    disabled = false,
    searchable = true,
    searchPlaceholder = '検索...',
    emptyMessage = '該当するメンバーが見つかりません',
    maxListHeight,
    departments,
    filterFn,
    isMemberDisabled,
    renderOption,
    className,
    style,
    id,
  } = props;
  const ariaLabel = props['aria-label'];

  const autoId = useId();
  const triggerId = id ?? `cortex-mp-${autoId}`;
  const listboxId = `${triggerId}-listbox`;
  const labelId = label ? `${triggerId}-label` : undefined;
  const helperId = !error && helperText ? `${triggerId}-helper` : undefined;
  const errorId = error ? `${triggerId}-error` : undefined;

  const [open, setOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const popoverRect = useFloatingPosition(triggerRef, open, popoverRef);

  const selectedIds = useMemo<readonly string[]>(() => {
    if (props.multiple === true) return props.value;
    return props.value === null ? [] : [props.value];
  }, [props]);

  const scopedMembers = useMemo<readonly Member[]>(() => {
    const allowedEmailSet =
      allowedEmails === undefined
        ? undefined
        : new Set(allowedEmails.map((email) => email.toLowerCase()));
    const allowedDepartmentSet = departments === undefined ? undefined : new Set(departments);

    return fetchedMembers
      .filter((m) => allowedEmailSet === undefined || allowedEmailSet.has(m.id.toLowerCase()))
      .filter(
        (m) =>
          allowedDepartmentSet === undefined ||
          (m.department !== undefined && allowedDepartmentSet.has(m.department)),
      )
      .map((m) => ({
        ...m,
        disabled: isMemberDisabled?.(m) ?? m.disabled,
      }));
  }, [fetchedMembers, allowedEmails, departments, isMemberDisabled]);

  const filteredMembers = useMemo<Member[]>(() => {
    const trimmed = query.trim();
    if (trimmed === '') return [...scopedMembers];
    const lowered = trimmed.toLowerCase();
    const predicate =
      filterFn ??
      ((m: Member, q: string): boolean => {
        const haystack = [m.name, m.description ?? '', m.email ?? '', m.id, m.department ?? '']
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    return scopedMembers.filter((m) => predicate(m, lowered));
  }, [scopedMembers, query, filterFn]);

  // open 切り替え時に active index を整える
  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
      return;
    }
    // 先頭の選択済みアイテムにフォーカスを置く
    const initial = filteredMembers.findIndex((m) => selectedIds.includes(m.id) && !m.disabled);
    const firstEnabled = filteredMembers.findIndex((m) => !m.disabled);
    setActiveIndex(initial >= 0 ? initial : firstEnabled);
  }, [open, filteredMembers, selectedIds]);

  // 検索クエリ変更時に active index をフィルタ後の先頭へ
  useEffect(() => {
    if (!open) return;
    const firstEnabled = filteredMembers.findIndex((m) => !m.disabled);
    setActiveIndex(firstEnabled);
  }, [query, open, filteredMembers]);

  // 外側クリックで閉じる (popover は portal で body 直下に描画するため、popoverRef も判定に含める)
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent): void => {
      const root = rootRef.current;
      const popover = popoverRef.current;
      if (root === null) return;
      if (!(event.target instanceof Node)) return;
      if (root.contains(event.target)) return;
      if (popover !== null && popover.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  // 開いた直後に検索フォーカス
  useEffect(() => {
    if (open && searchable) {
      searchRef.current?.focus();
    }
  }, [open, searchable]);

  // アクティブ行を可視領域へ
  useEffect(() => {
    if (!open) return;
    if (activeIndex < 0) return;
    const node = optionRefs.current[activeIndex];
    if (node !== null && node !== undefined && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open]);

  const closeAndFocusTrigger = useCallback((): void => {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  }, []);

  const commitSelection = useCallback(
    (member: Member): void => {
      if (member.disabled === true) return;
      if (props.multiple === true) {
        const exists = props.value.includes(member.id);
        const next = exists
          ? props.value.filter((id) => id !== member.id)
          : [...props.value, member.id];
        props.onChange(next);
        return;
      }
      props.onChange(member.id);
      closeAndFocusTrigger();
    },
    [props, closeAndFocusTrigger],
  );

  const moveActive = useCallback(
    (direction: 1 | -1): void => {
      if (filteredMembers.length === 0) {
        setActiveIndex(-1);
        return;
      }
      setActiveIndex((current) => {
        const start = current < 0 ? (direction === 1 ? -1 : filteredMembers.length) : current;
        for (let step = 1; step <= filteredMembers.length; step += 1) {
          const next = (start + direction * step + filteredMembers.length) % filteredMembers.length;
          const candidate = filteredMembers[next];
          if (candidate !== undefined && candidate.disabled !== true) {
            return next;
          }
        }
        return current;
      });
    },
    [filteredMembers],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>): void => {
      if (disabled) return;
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          if (!open) {
            setOpen(true);
            return;
          }
          moveActive(1);
          return;
        }
        case 'ArrowUp': {
          event.preventDefault();
          if (!open) {
            setOpen(true);
            return;
          }
          moveActive(-1);
          return;
        }
        case 'Enter': {
          if (!open) return;
          event.preventDefault();
          const target = filteredMembers[activeIndex];
          if (target !== undefined) commitSelection(target);
          return;
        }
        case 'Escape': {
          if (!open) return;
          event.preventDefault();
          closeAndFocusTrigger();
          return;
        }
        case 'Tab': {
          if (open) setOpen(false);
          return;
        }
        default:
          return;
      }
    },
    [
      disabled,
      open,
      moveActive,
      filteredMembers,
      activeIndex,
      commitSelection,
      closeAndFocusTrigger,
    ],
  );

  const removeSelected =
    props.multiple === true
      ? (event: ReactMouseEvent<HTMLButtonElement>, memberId: string): void => {
          event.stopPropagation();
          props.onChange(props.value.filter((id) => id !== memberId));
        }
      : undefined;

  const toggleOpen = useCallback((): void => {
    if (disabled) return;
    setOpen((prev) => !prev);
  }, [disabled]);

  const memberById = useMemo<Map<string, Member>>(() => {
    const map = new Map<string, Member>();
    for (const member of scopedMembers) map.set(member.id, member);
    return map;
  }, [scopedMembers]);

  const popoverStyle: CSSProperties | undefined = (() => {
    const base: CSSProperties = {};
    if (maxListHeight !== undefined) {
      (base as Record<string, string>)['--cortex-mp-list-max-height'] =
        `${String(maxListHeight)}px`;
    }
    if (popoverRect !== null) {
      base.top = `${String(popoverRect.top)}px`;
      base.left = `${String(popoverRect.left)}px`;
      base.width = `${String(popoverRect.width)}px`;
      // 上下どちらにも収まらない時は内部スクロールに切り替える（画面外へ出さない）
      base.maxHeight = `${String(popoverRect.maxHeight)}px`;
    }
    base.zIndex = 9999;
    return base;
  })();

  const handleSearchChange = (value: string): void => {
    setQuery(value);
  };

  return (
    <div
      ref={rootRef}
      className={`cortex-mp-root${className === undefined ? '' : ` ${className}`}`}
      style={style}
    >
      {label !== undefined && (
        <span id={labelId} className="cortex-mp-label">
          {label}
        </span>
      )}
      <div
        ref={triggerRef}
        id={triggerId}
        className="cortex-mp-trigger"
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-labelledby={labelId}
        aria-label={labelId === undefined ? ariaLabel : undefined}
        aria-disabled={disabled}
        aria-invalid={error !== undefined ? true : undefined}
        aria-describedby={errorId ?? helperId}
        data-open={open ? 'true' : 'false'}
        data-error={error !== undefined ? 'true' : 'false'}
        onClick={toggleOpen}
        onKeyDown={handleKeyDown}
      >
        <span className="cortex-mp-trigger-content">
          {renderTriggerContent({
            multiple: props.multiple === true,
            selectedIds,
            memberById,
            placeholder,
            disabled,
            onRemove: removeSelected,
          })}
        </span>
        <svg
          className="cortex-mp-caret"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open &&
        createPortal(
          <div ref={popoverRef} className="cortex-mp-popover" style={popoverStyle}>
            {searchable && (
              <input
                ref={searchRef}
                type="text"
                className="cortex-mp-search"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                aria-controls={listboxId}
                aria-autocomplete="list"
              />
            )}
            {filteredMembers.length === 0 ? (
              <div className="cortex-mp-empty" role="status">
                {emptyMessage}
              </div>
            ) : (
              <ul
                id={listboxId}
                role="listbox"
                aria-multiselectable={props.multiple === true}
                className="cortex-mp-list"
              >
                {filteredMembers.map((member, index) => {
                  const selected = selectedIds.includes(member.id);
                  const active = index === activeIndex;
                  const optionAccessibleName = [
                    member.name,
                    member.department,
                    member.description ?? member.email,
                  ]
                    .filter((value): value is string => value !== undefined && value !== '')
                    .join(' ');
                  return (
                    <li
                      key={member.id}
                      ref={(node) => {
                        optionRefs.current[index] = node;
                      }}
                      role="option"
                      aria-selected={selected}
                      aria-label={optionAccessibleName}
                      aria-disabled={member.disabled === true ? true : undefined}
                      data-active={active ? 'true' : 'false'}
                      data-selected={selected ? 'true' : 'false'}
                      data-disabled={member.disabled === true ? 'true' : 'false'}
                      className="cortex-mp-option"
                      onMouseEnter={() => {
                        if (member.disabled !== true) setActiveIndex(index);
                      }}
                      onClick={() => {
                        commitSelection(member);
                      }}
                    >
                      {renderOption !== undefined
                        ? renderOption(member)
                        : defaultRenderOption(member, selected)}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>,
          document.body,
        )}

      {error !== undefined && (
        <div id={errorId} className="cortex-mp-error" role="alert">
          {error}
        </div>
      )}
      {error === undefined && helperText !== undefined && (
        <div id={helperId} className="cortex-mp-helper">
          {helperText}
        </div>
      )}
    </div>
  );
}
